/**
 * Capacitor Native Integration for RSSio
 *
 * Handles:
 * - Detecting native vs. web context
 * - Server URL configuration (first-run "connect" screen)
 * - Patching fetch() to route API calls to the remote server
 * - Loading background-fetched articles on app resume
 * - Sharing credentials with the native Android sync service
 */

import { Capacitor, registerPlugin } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { App } from "@capacitor/app";
import { Network } from "@capacitor/network";
import { LocalNotifications } from "@capacitor/local-notifications";
import { bulkSaveArticlesOffline } from "./db.ts";
import { feedItems, invalidateMemoCache, state } from "./state.ts";
import { normalizeArticleId, sanitizeFeedItemsForSubscriptionUrl } from "./utils.ts";

interface NativeSyncPlugin {
    configure(options: {
        serverUrl?: string;
        backgroundToken?: string;
        clearBackgroundToken?: boolean;
    }): Promise<void>;
    startSync(options: { fetchArticles?: boolean; downloadYouTube?: boolean }): Promise<void>;
    queueYouTubeDownloadItem(options: { articleId: string; youtubeUrl: string }): Promise<{ queued?: boolean }>;
    peekBgFetchedArticles(): Promise<BgFetchResult | null>;
    ackBgFetchedArticles(options: { batchId: number }): Promise<void>;
    consumeDownloadedYouTubeVideos(): Promise<Record<string, unknown> | null>;
    ackDownloadedYouTubeVideo(options: { articleId: string }): Promise<void>;
    purgeYouTubeVideo(options: { articleId: string }): Promise<void>;
    consumePendingLaunchShareIntent(): Promise<{ url?: string } | null>;
    notifyShareSummaryStatus(options: { status: "success" | "error"; message?: string }): Promise<void>;
    playOfflineVideo(options: {
        articleId: string;
        filePath: string;
        title?: string;
        startPositionSeconds?: number;
    }): Promise<void>;
    getOfflineVideoPlaybackPositions(): Promise<Record<string, number> | null>;
    closeHostActivity(): Promise<void>;
    clearShareIntent(): Promise<void>;
    setStatusBarTheme(options: { isDark: boolean }): Promise<void>;
}

interface NativeUpdatePlugin {
    getInstalledVersion(): Promise<{ versionCode: number; versionName: string }>;
    downloadAndInstall(options: { apkUrl: string; sha256: string }): Promise<void>;
}

export const NativeSync = registerPlugin<NativeSyncPlugin>("RssioSync");
export const NativeUpdate = registerPlugin<NativeUpdatePlugin>("RssioUpdate");
export const BG_FETCH_MERGED_EVENT = "rssio:bg-fetch-merged";
export const NATIVE_RESUMED_EVENT = "rssio:native-resumed";
export const NATIVE_RESUME_START_EVENT = "rssio:native-resume-start";

// ─── Public state ───

export const isNative = Capacitor.isNativePlatform();
if (isNative && typeof document !== "undefined") {
    document.documentElement.classList.add("capacitor-native");
    if (Capacitor.getPlatform() === "android") {
        document.documentElement.classList.add("native-android");
    }
}
let serverUrl = "";
let backgroundToken = "";
let shareIntentMode = false;

export interface PendingSharedLink {
    text: string;
    title: string;
    target?: "readLater" | "favorites";
    mode?: "summaryCard";
    autoSave?: boolean;
}

/** The remote server base URL (empty string in web mode). */
export function getServerUrl(): string {
    return serverUrl;
}

export function getBackgroundToken(): string {
    return backgroundToken;
}

export function isShareIntentMode(): boolean {
    return isNative && shareIntentMode;
}

export function clearShareIntentMode(): void {
    shareIntentMode = false;
    document.body.classList.remove("share-intent-mode");
    if (isNative) {
        void NativeSync.clearShareIntent().catch(() => { });
    }
}

export async function syncNativeStatusBarTheme(isDark: boolean): Promise<void> {
    if (!isNative) return;
    try {
        await NativeSync.setStatusBarTheme({ isDark });
    } catch {
        // Native plugin might not be ready yet
    }
}

// ─── Server URL management ───

const SERVER_URL_KEY = "rssio_server_url";
const BACKGROUND_TOKEN_KEY = "rssio_background_token";
const PENDING_SHARED_LINK_KEY = "rssio_pending_shared_link";
const OFFLINE_VIDEO_POSITION_KEY = "rssio-offline-video-position:";

async function loadServerUrl(): Promise<string | null> {
    if (!isNative) return null;
    const { value } = await Preferences.get({ key: SERVER_URL_KEY });
    return value;
}

async function loadBackgroundToken(): Promise<string | null> {
    if (!isNative) return null;
    const { value } = await Preferences.get({ key: BACKGROUND_TOKEN_KEY });
    return value;
}

async function savePendingSharedLink(link: PendingSharedLink): Promise<void> {
    if (!isNative) return;
    await Preferences.set({
        key: PENDING_SHARED_LINK_KEY,
        value: JSON.stringify(link),
    });
}

export async function consumePendingSharedLink(): Promise<PendingSharedLink | null> {
    if (!isNative) return null;

    const { value } = await Preferences.get({ key: PENDING_SHARED_LINK_KEY });
    if (!value) {
        return null;
    }

    await Preferences.remove({ key: PENDING_SHARED_LINK_KEY });
    try {
        return JSON.parse(value) as PendingSharedLink;
    } catch {
        return null;
    }
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function syncNativeSyncConfig(options: { clearBackgroundToken?: boolean } = {}): Promise<void> {
    console.log("[Capacitor] syncNativeSyncConfig ENTRY: backgroundToken length=", backgroundToken.length, "clearBackgroundToken=", options.clearBackgroundToken);
    if (!isNative) return;

    // Don't configure with blank credentials - this would cancel periodic sync
    if (!backgroundToken && !options.clearBackgroundToken) {
        console.log("[Capacitor] syncNativeSyncConfig: skipping - no backgroundToken and not clearing");
        return;
    }

    console.log("[Capacitor] syncNativeSyncConfig: serverUrl=", !!serverUrl, "backgroundToken=", !!backgroundToken, "backgroundToken.length=", backgroundToken.length);
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            // Double-check: don't call configure with blank backgroundToken (unless explicitly clearing)
            if (!backgroundToken && !options.clearBackgroundToken) {
                console.log("[Capacitor] syncNativeSyncConfig: guard check failed, skipping configure");
                return;
            }
            await NativeSync.configure({
                serverUrl,
                backgroundToken,
                clearBackgroundToken: options.clearBackgroundToken === true,
            });
            console.log("[Capacitor] NativeSync.configure called successfully");
            if (attempt > 1) {
                console.log(`[Capacitor] Native sync config synced on attempt ${attempt}`);
            }
            return;
        } catch (e) {
            if (attempt === maxAttempts) {
                console.log("[Capacitor] Native sync config sync failed:", e);
                return;
            }
            await delay(500 * attempt);
        }
    }
}

export async function startForegroundSync(options: {
    fetchArticles?: boolean;
    downloadYouTube?: boolean;
}): Promise<void> {
    if (!isNative) return;

    try {
        await NativeSync.startSync({
            fetchArticles: options.fetchArticles === true,
            downloadYouTube: options.downloadYouTube === true,
        });
    } catch (e) {
        console.error("[Capacitor] Failed to start native sync:", e);
    }
}

async function saveBackgroundToken(token: string): Promise<void> {
    if (!isNative) return;
    backgroundToken = token;
    console.log("[Capacitor] saveBackgroundToken: token length=", token.length);
    await Preferences.set({ key: BACKGROUND_TOKEN_KEY, value: token });
    await syncNativeSyncConfig();
}

export async function ensureBackgroundToken(): Promise<string> {
    if (!isNative || !serverUrl) {
        console.log("[Capacitor] ensureBackgroundToken early return: isNative=", isNative, "serverUrl=", !!serverUrl);
        return "";
    }
    if (backgroundToken) {
        console.log("[Capacitor] ensureBackgroundToken: using existing token, length=", backgroundToken.length);
        await syncNativeSyncConfig();
        return backgroundToken;
    }

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            console.log(`[Capacitor] ensureBackgroundToken: fetching new token, attempt ${attempt}`);
            const resp = await fetch(`${serverUrl}/auth/background-token`, {
                credentials: "include",
            });

            if (!resp.ok) {
                console.log(`[Capacitor] Background token request failed (attempt ${attempt}):`, resp.status, resp.statusText);
                if (attempt === maxAttempts) return "";
                await delay(500 * attempt);
                continue;
            }

            const data = await resp.json();
            console.log(`[Capacitor] Background token response:`, data);
            if (!data.backgroundToken) {
                console.log(`[Capacitor] Background token response missing token (attempt ${attempt})`);
                if (attempt === maxAttempts) return "";
                await delay(500 * attempt);
                continue;
            }

            await saveBackgroundToken(data.backgroundToken);
            console.log("[Capacitor] Background token refreshed from session");
            return backgroundToken;
        } catch (e) {
            console.error(`[Capacitor] Background token request error (attempt ${attempt}):`, e);
            if (attempt === maxAttempts) return "";
            await delay(500 * attempt);
        }
    }
    return "";
}

export async function clearBackgroundToken(): Promise<void> {
    if (!isNative) return;
    backgroundToken = "";
    await Preferences.remove({ key: BACKGROUND_TOKEN_KEY });
    await syncNativeSyncConfig({ clearBackgroundToken: true });
}

export async function playNativeOfflineVideo(options: {
    articleId: string;
    filePath: string;
    title?: string;
    startPositionSeconds?: number;
}): Promise<void> {
    if (!isNative) return;

    try {
        await NativeSync.playOfflineVideo(options);
    } catch (e) {
        console.error("[Capacitor] Failed to play offline video natively:", e);
    }
}

export async function syncNativeOfflineVideoPlaybackPositions(): Promise<void> {
    if (!isNative) return;

    try {
        const positions = await NativeSync.getOfflineVideoPlaybackPositions();
        if (!positions) {
            return;
        }

        Object.entries(positions).forEach(([articleId, rawValue]) => {
            const positionSeconds = Number(rawValue || 0);
            if (!articleId) {
                return;
            }

            if (!Number.isFinite(positionSeconds) || positionSeconds <= 1) {
                localStorage.removeItem(OFFLINE_VIDEO_POSITION_KEY + articleId);
                return;
            }

            localStorage.setItem(
                OFFLINE_VIDEO_POSITION_KEY + articleId,
                String(Math.floor(positionSeconds)),
            );
        });
    } catch (e) {
        console.error("[Capacitor] Failed to sync native offline video positions:", e);
    }
}

export async function saveServerUrl(url: string): Promise<void> {
    const cleaned = url.replace(/\/+$/, "");
    serverUrl = cleaned;
    await Preferences.set({ key: SERVER_URL_KEY, value: cleaned });
    await syncNativeSyncConfig();
}

// ─── Fetch patching ───
// In native mode, all relative-path fetch calls (e.g. "/auth/user") need
// the remote server URL prepended. This single patch covers every call in
// the codebase without touching existing modules.

function patchFetch(): void {
    if (!isNative || !serverUrl) return;

    const originalFetch = window.fetch.bind(window);

    window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        if (typeof input === "string" && input.startsWith("/")) {
            input = serverUrl + input;
        } else if (input instanceof Request && input.url.startsWith("/")) {
            input = new Request(serverUrl + input.url, input);
        }
        return originalFetch(input, init);
    };
}

async function handleIncomingAppUrl(url: string): Promise<void> {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return;
    }

    if (parsed.protocol !== "rssio:") {
        return;
    }

    if (parsed.host === "auth" && parsed.pathname === "/callback") {
        const token = parsed.searchParams.get("token");
        if (!token) {
            return;
        }

        try {
            const resp = await fetch(`${serverUrl}/auth/token-exchange?token=${encodeURIComponent(token)}`, {
                credentials: "include",
            });
            if (resp.ok) {
                const data = await resp.json();
                if (data.backgroundToken) {
                    await saveBackgroundToken(data.backgroundToken);
                }
                console.log("[Capacitor] Token exchange successful — reloading app");
                window.location.reload();
            } else {
                console.error("[Capacitor] Token exchange failed:", resp.status);
            }
        } catch (e) {
            console.error("[Capacitor] Token exchange error:", e);
        }
        return;
    }

    if (parsed.host === "share") {
        const text = parsed.searchParams.get("text") || "";
        const title = parsed.searchParams.get("title") || "";
        const target = parsed.searchParams.get("target");
        const mode = parsed.searchParams.get("mode");
        const autoSave = parsed.searchParams.get("autosave") === "1";
        if (!text.trim()) {
            return;
        }

        shareIntentMode = true;
        document.body.classList.add("share-intent-mode");
        await savePendingSharedLink({
            text,
            title,
            target: target === "readLater" || target === "favorites" ? target : undefined,
            mode: mode === "summaryCard" ? "summaryCard" : undefined,
            autoSave,
        });

        setTimeout(() => {
            window.dispatchEvent(new Event("rssio:pending-share"));
        }, 0);
    }
}

// ─── Background-fetched article loading ───

export interface BgFetchResult {
    batchId: number;
    feedItems: Record<string, any[]>;
    fetchedAt: number;
    articleCount: number;
}

let backgroundMergeInFlight: Promise<void> | null = null;

function waitForIdle(): Promise<void> {
    return new Promise((resolve) => {
        const requestIdleCallbackFn = (window as any).requestIdleCallback;
        if (typeof requestIdleCallbackFn === "function") {
            requestIdleCallbackFn(() => resolve(), { timeout: 250 });
            return;
        }

        window.setTimeout(resolve, 0);
    });
}

function yieldToUi(): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, 0));
}

function mergeFeedArticleLists(existingArticles: import("./state.ts").Article[], incomingArticles: import("./state.ts").Article[]): import("./state.ts").Article[] {
    const merged = new Map<string, import("./state.ts").Article>();

    existingArticles.forEach((article) => {
        const articleId = normalizeArticleId(article);
        if (articleId) {
            merged.set(articleId, article);
        }
    });

    incomingArticles.forEach((article) => {
        const articleId = normalizeArticleId(article);
        if (!articleId) {
            return;
        }

        const current = merged.get(articleId);
        merged.set(articleId, current ? { ...current, ...article } : article);
    });

    return Array.from(merged.values()).sort(
        (left, right) =>
            new Date(right?.pubDate || 0).getTime() -
            new Date(left?.pubDate || 0).getTime(),
    );
}

async function persistBackgroundArticlesToIndexedDB(bg: BgFetchResult): Promise<number> {
    const recordsToSave: import("./db.ts").OfflineArticleRecord[] = [];
    for (const [feedId, articles] of Object.entries(bg.feedItems || {})) {
        for (const article of articles || []) {
            const articleId = normalizeArticleId(article);
            if (!articleId) {
                continue;
            }

            recordsToSave.push({
                id: articleId,
                guid: (typeof article.guid === "string" ? article.guid : undefined) || articleId,
                title: article.title,
                author: article.author,
                pubDate: article.pubDate,
                link: article.link,
                content: article.content || article.description || "",
                feedTitle: article.feedTitle || "",
                imageUrl: article.imageUrl || article["media:thumbnail"]?.$?.url || article["media:content"]?.$?.url,
                feedId,
                savedAt: bg.fetchedAt,
            });
        }
    }

    if (recordsToSave.length > 0) {
        const CHUNK_SIZE = 100;
        for (let i = 0; i < recordsToSave.length; i += CHUNK_SIZE) {
            const chunk = recordsToSave.slice(i, i + CHUNK_SIZE);
            await bulkSaveArticlesOffline(chunk);
            if (i + CHUNK_SIZE < recordsToSave.length) {
                await yieldToUi();
            }
        }
    }

    return recordsToSave.length;
}

/**
 * Ask the native sync service for the latest staged article batch. The batch
 * remains staged until JS explicitly acknowledges it after a successful merge.
 */
export async function loadBgFetchedArticles(): Promise<BgFetchResult | null> {
    if (!isNative) {
        console.log("[Capacitor] Not native, skipping background article load");
        return null;
    }

    try {
        console.log("[Capacitor] Asking native sync for staged fetched articles...");
        const result = await NativeSync.peekBgFetchedArticles();
        if (result) {
            console.log(
                `[Capacitor] Got staged batch ${result.batchId} with ${result.articleCount} background-fetched articles`,
            );
        } else {
            console.log("[Capacitor] Native sync returned null (no articles)");
        }
        return result || null;
    } catch (e) {
        console.error("[Capacitor] Error loading background articles:", e);
        return null;
    }
}

// ─── Notification Permissions ───

async function requestNotificationPermission(): Promise<void> {
    if (!isNative) return;

    try {
        const { display } = await LocalNotifications.checkPermissions();
        console.log(`[Capacitor] Notification permission status: ${display}`);
        if (display === "prompt" || display === "prompt-with-rationale") {
            console.log("[Capacitor] Requesting notification permission...");
            const result = await LocalNotifications.requestPermissions();
            console.log(`[Capacitor] Notification permission result: ${result.display}`);
        }
    } catch (e) {
        console.log("[Capacitor] Notification permission request failed:", e);
    }
}

// ─── App lifecycle ───

function setupAppLifecycle(): void {
    if (!isNative) return;

    // Handle deep link from OAuth callback (rssio://auth/callback?token=xxx)
    App.addListener("appUrlOpen", async ({ url }) => {
        console.log("[Capacitor] Deep link received:", url);
        await handleIncomingAppUrl(url);
    });

    let lastResumeAt = 0;
    // On resume, merge any background-fetched articles
    App.addListener("resume", async () => {
        const now = Date.now();
        if (now - lastResumeAt < 10000) {
            console.log("[Capacitor] Debouncing rapid app resume");
            return;
        }
        lastResumeAt = now;
        console.log("[Capacitor] App resumed — checking for background-fetched articles");
        state.isMergingBackgroundArticles = true;
        window.dispatchEvent(new Event(NATIVE_RESUME_START_EVENT));
        try {
        await ensureBackgroundToken();
        await syncNativeSyncConfig();
        await syncNativeOfflineVideoPlaybackPositions();
        await mergeBackgroundFetchedArticles();
        } finally {
            state.isMergingBackgroundArticles = false;
        }
        window.dispatchEvent(new Event(NATIVE_RESUMED_EVENT));
    });

    // Inactivity also includes notification shade, permission dialogs and native
    // playback. Do not start a foreground service for those transitions. Article
    // fetching is already scheduled by WorkManager; queued video downloads have
    // their own explicit foreground trigger.

    // Network status
    Network.addListener("networkStatusChange", (status) => {
        console.log(`[Capacitor] Network: ${status.connected ? "online" : "offline"}`);
        window.dispatchEvent(new Event(status.connected ? "online" : "offline"));
    });
}

export async function mergeBackgroundFetchedArticles(): Promise<void> {
    if (backgroundMergeInFlight) {
        return backgroundMergeInFlight;
    }

    backgroundMergeInFlight = (async () => {
        console.log("[Capacitor] Starting merge of background-fetched articles...");
        const bg = await loadBgFetchedArticles();
        if (!bg || bg.articleCount <= 0) {
            console.log("[Capacitor] No background articles to merge");
            return;
        }

        console.log(`[Capacitor] Found ${bg.articleCount} background-fetched articles, merging...`);
        try {
            const cacheRaw = localStorage.getItem("rssio-feed-cache-v2");
            const cache = cacheRaw ? JSON.parse(cacheRaw) : { feedItems: {}, feedCounts: {} };
            const existing = cache.feedItems || {};
            cache.feedCounts = cache.feedCounts || {};

            const mergedFeedIds: string[] = [];
            const sanitizedBgFeedItems: Record<string, any[]> = {};
            let actualNewArticleCount = 0;
            let processedFeeds = 0;
            for (const [feedId, articles] of Object.entries(bg.feedItems || {})) {
                const currentArticles = Array.isArray(existing[feedId]) ? existing[feedId] : [];
                const subscription = state.subscriptions.find((sub) => sub.id === feedId);
                const sanitizedCurrentArticles = sanitizeFeedItemsForSubscriptionUrl(
                    subscription?.url,
                    currentArticles,
                );
                const sanitizedIncomingArticles = sanitizeFeedItemsForSubscriptionUrl(
                    subscription?.url,
                    Array.isArray(articles) ? articles : [],
                );
                const previousArticleIds = new Set(
                    sanitizedCurrentArticles
                        .map((article) => normalizeArticleId(article))
                        .filter(Boolean),
                );
                const mergedArticles = mergeFeedArticleLists(
                    sanitizedCurrentArticles,
                    sanitizedIncomingArticles,
                );
                existing[feedId] = mergedArticles;
                cache.feedCounts[feedId] = mergedArticles.length;
                feedItems[feedId] = mergedArticles;
                state.feedCounts[feedId] = mergedArticles.length;
                sanitizedBgFeedItems[feedId] = sanitizedIncomingArticles;
                actualNewArticleCount += mergedArticles.reduce((count, article) => {
                    const articleId = normalizeArticleId(article);
                    return articleId && !previousArticleIds.has(articleId) ? count + 1 : count;
                }, 0);
                mergedFeedIds.push(feedId);
                processedFeeds += 1;

                if (processedFeeds % 5 === 0) {
                    await yieldToUi();
                }
            }

            cache.feedItems = existing;
            localStorage.setItem("rssio-feed-cache-v2", JSON.stringify(cache));
            localStorage.setItem("rssio-feed-cache-v2-time", String(bg.fetchedAt));
            window.feedItems = feedItems;
            state.lastRefreshTime = Math.max(state.lastRefreshTime || 0, Number(bg.fetchedAt || Date.now()));
            invalidateMemoCache();
            console.log("[Capacitor] Merged background articles into localStorage cache");

            // Persist to IndexedDB and acknowledge staged batch non-blockingly
            void (async () => {
                try {
                    await waitForIdle();
                    const savedToIndexedDB = await persistBackgroundArticlesToIndexedDB({
                        ...bg,
                        feedItems: sanitizedBgFeedItems,
                        articleCount: actualNewArticleCount,
                    });
                    console.log(`[Capacitor] Saved ${savedToIndexedDB} background articles to IndexedDB`);

                    await NativeSync.ackBgFetchedArticles({ batchId: bg.batchId });
                    console.log(`[Capacitor] Acknowledged staged background batch ${bg.batchId}`);
                } catch (err) {
                    console.error("[Capacitor] Failed to persist background articles to IndexedDB:", err);
                }
            })();

            window.dispatchEvent(
                new CustomEvent(BG_FETCH_MERGED_EVENT, {
                    detail: {
                        feedIds: mergedFeedIds,
                        articleCount: actualNewArticleCount,
                        fetchedAt: bg.fetchedAt,
                    },
                }),
            );
        } catch (e) {
            console.error("[Capacitor] Failed to merge bg articles:", e);
        }
    })().finally(() => {
        backgroundMergeInFlight = null;
    });

    return backgroundMergeInFlight;
}

// ─── Bootstrap ───

/**
 * Initialize Capacitor integration. Must be called before initApp().
 * In web mode this is a no-op.
 */
export async function initCapacitor(): Promise<void> {
    if (!isNative) return;

    console.log("[Capacitor] Running in native mode");
    shareIntentMode = false;
    document.body.classList.remove("share-intent-mode");

    // Hardcoded server URL for Android app
    const HARDCODED_SERVER_URL = "https://rss.chrismyll.gr";

    // Load or prompt for server URL
    let url = await loadServerUrl();
    if (!url) {
        // Use hardcoded URL instead of showing connect screen
        url = HARDCODED_SERVER_URL;
        await saveServerUrl(url);
        console.log("[Capacitor] Using hardcoded server URL:", url);
    } else {
        serverUrl = url;
    }
    backgroundToken = (await loadBackgroundToken()) || "";
    console.log("[Capacitor] initCapacitor: loaded backgroundToken length=", backgroundToken.length);
    await ensureBackgroundToken();
    // Note: ensureBackgroundToken() calls syncNativeSyncConfig() internally
    // after successfully obtaining a background token, so no additional call needed here.

    console.log(`[Capacitor] Server URL: ${serverUrl}, backgroundToken length: ${backgroundToken.length}`);

    // Patch fetch so all relative API calls hit the remote server
    patchFetch();

    // Start lifecycle listeners
    setupAppLifecycle();

    // Request notification permission for background sync notifications (Android 13+)
    await requestNotificationPermission();

    // Sync resolved theme with native Android status bar
    const isDark = document.documentElement.getAttribute("data-theme") === "dark";
    void syncNativeStatusBarTheme(isDark);

    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
        await handleIncomingAppUrl(launchUrl.url);
        try {
            await NativeSync.consumePendingLaunchShareIntent();
        } catch {
            // Best-effort cleanup of the native fallback stash.
        }
    } else {
        try {
            const pendingLaunchShare = await NativeSync.consumePendingLaunchShareIntent();
            if (pendingLaunchShare?.url) {
                await handleIncomingAppUrl(pendingLaunchShare.url);
            }
        } catch {
            // Ignore missing native fallback support.
        }
    }

    // Delay background article consumption until initApp has loaded
    // subscriptions, so cold-start merge can sanitize and present them
    // against the real feed list before first render.
    await syncNativeOfflineVideoPlaybackPositions();

    console.log("[Capacitor] Initialization complete");
}
